import { Button, makeStyles, TabList, Tab, Title2, Tooltip, Toaster, useToastController, Toast, ToastTitle, ToastBody, Toolbar, Table, TableHeader, TableHeaderCell, TableBody, TableCell, TableRow, Link, Dropdown, Option, Switch, InfoLabel, Card, CardHeader, Subtitle2, Popover, PopoverTrigger, PopoverSurface, Input } from "@fluentui/react-components";
import { AddFilled, BeakerFilled, bundleIcon, DeleteFilled, DeleteRegular, EditFilled, EditRegular, KeyResetFilled, KeyResetRegular, MoneySettingsFilled, MoneySettingsRegular, PeopleTeamFilled, PeopleTeamRegular, PlugConnectedSettingsFilled, PlugConnectedSettingsRegular, InfoFilled, InfoRegular, Info20Filled } from "@fluentui/react-icons";
import React, { useEffect } from "react";
import ReassignApiKeyDialog from "../dialogs/ReassignApiKeyDialog";
import AddStaffMemberDrawer from "../drawers/AddStaffMemberDrawer";

const useStyles = makeStyles({
    root: {
        display: "flex",
        gap: "2rem"
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        width: "100%",
    },
    cardTitle: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem"
    },
    columnTable: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "1rem",

        "& > *": {
            flex: "0 0 calc(50% - 0.5rem)",
            minWidth: "200px"
        }
    }
});

const InfoIcon = bundleIcon( InfoFilled, InfoRegular );
const PeopleTeam = bundleIcon( PeopleTeamFilled, PeopleTeamRegular );
const PlugConnectedSettings = bundleIcon( PlugConnectedSettingsFilled, PlugConnectedSettingsRegular );
const EditIcon = bundleIcon( EditFilled, EditRegular );
const KeyResetIcon = bundleIcon( KeyResetFilled, KeyResetRegular );
const MoneySettingsIcon = bundleIcon( MoneySettingsFilled, MoneySettingsRegular );
const DeleteIcon = bundleIcon( DeleteFilled, DeleteRegular );

export default function SettingsPage()
{
    const styles = useStyles();
    const [ selectedTab, setSelectedTab ] = React.useState( "details" );
    const [ subtitleIcon, setSubtitleIcon ] = React.useState( <InfoIcon /> );
    const [ subtitle, setSubtitle ] = React.useState( "Details" );
    const [ storeNamePopoverOpen, setStoreNamePopoverOpen ] = React.useState( false );

    const [ addStaffMemberDrawerOpen, setAddStaffMemberDrawerOpen ] = React.useState( false );
    const [ reassignApiKeyDialogOpen, setReassignApiKeyDialogOpen ] = React.useState( false );
    const { dispatchToast } = useToastController( "settingsToaster" );

    useEffect(
        () => {
            document.title = "Settings - Omnivend";
        },
        []
    );

    useEffect(
        () => {
            setSubtitle(
                selectedTab === "details" ? "Details" :
                selectedTab === "staff" ? "Staff" :
                selectedTab === "api" ? "API Access" :
                selectedTab === "subscriptions" ? "Subscriptions" : ""
            );
            setSubtitleIcon(
                selectedTab === "details" ? <InfoFilled /> :
                selectedTab === "staff" ? <PeopleTeamFilled /> :
                selectedTab === "api" ? <PlugConnectedSettingsFilled /> :
                selectedTab === "subscriptions" ? <MoneySettingsFilled /> : <InfoFilled />
            );
        },
        [ selectedTab ]
    );

    const triggerReassignApiKey = () => {
        dispatchToast(
            <Toast>
                <ToastTitle>API Key Reassigned</ToastTitle>
                <ToastBody>Your API key has been successfully reassigned. Please update your applications or services with the new API key.</ToastBody>
            </Toast>,
            {
                intent: "success",
                position: "top-end",
                timeout: 5000
            }
        );
    }

    const createRoleDropdown = ( currentRole: string ) : React.ReactNode => {
        return <Dropdown defaultSelectedOptions={ [ currentRole ] } defaultValue="CSR">
                <Option value="csr" text="CSR">
                    <div style={{ display: "flex", gap: "0.5rem", flexDirection: "column" }}>
                        <b>CSR</b>
                        Customer Service Representatives can view and manage sales and customers, but cannot edit store settings or manage products.
                    </div>
                </Option>
                <Option value="manager" text="Store Manager">
                    <div style={{ display: "flex", gap: "0.5rem", flexDirection: "column" }}>
                        <b>Store Manager</b>
                        Store Managers can view and edit store settings, manage products, and oversee sales.
                    </div>
                </Option>
                <Option value="admin" text="Administrator">
                    <div style={{ display: "flex", gap: "0.5rem", flexDirection: "column" }}>
                        <b>Administrator</b>
                        Administrators have full access and can manage all settings, including adding and removing managers.
                    </div>
                </Option>
            </Dropdown>;
    };

    return <>
        <div style={{ padding: "1rem", width: "100%" }}>
            <Title2 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>{subtitleIcon} <span>Settings | <span style={{ fontWeight: "normal" }}>{subtitle}</span></span></Title2>

            <div className={styles.root} style={{ marginTop: "2rem" }}>
                <TabList vertical appearance="subtle" defaultSelectedValue="details" onTabSelect={(e, data) => setSelectedTab( data.value )}>
                    <Tab value="details" icon={<InfoIcon />}>Details</Tab>
                    <Tab value="staff" icon={<PeopleTeam />}>Staff</Tab>
                    <Tab value="api" icon={<PlugConnectedSettings />}>API Access</Tab>
                    <Tab value="subscriptions" icon={<MoneySettingsIcon />}>Subscriptions</Tab>
                </TabList>

                <div className={styles.content}>
                    {
                        selectedTab === "details" ?
                            <div>
                                <Card appearance="outline" style={{ marginBottom: "1rem" }}>
                                    <CardHeader header={
                                        <div className={styles.cardTitle}>
                                            <Info20Filled />
                                            <Subtitle2>Overview</Subtitle2>
                                        </div>
                                    } />
                                    
                                    View and edit this store's details.
                                </Card>

                                <div className={styles.columnTable}>
                                    <div style={{ fontWeight: "500" }}>Store owner</div>
                                    <div>
                                        <Link href="https://my.secondlife.com/john.doe" target="_blank">
                                        John Doe
                                        </Link>
                                    </div>
                                    <div style={{ fontWeight: "500" }}>Store name</div>
                                    <div>
                                        Unnamed Store
                                        &nbsp;
                                        <Tooltip content="Edit Store Name" relationship="label">
                                            <Popover open={storeNamePopoverOpen} onOpenChange={(_, data) => { setStoreNamePopoverOpen( data.open )}}>
                                                <PopoverTrigger>
                                                    <Button icon={<EditIcon />} size="small" appearance="subtle" />
                                                </PopoverTrigger>
                                                <PopoverSurface>
                                                    <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
                                                        <div style={{ fontWeight: "500" }}>Edit Store Name</div>
                                                        <Input type="text" placeholder="Enter new store name" />
                                                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                                                            <Button size="small" onClick={() => { setStoreNamePopoverOpen( false )}}>Save</Button>
                                                        </div>
                                                    </div>
                                                </PopoverSurface>
                                            </Popover>
                                        </Tooltip>
                                    </div>
                                    <div style={{ fontWeight: "500" }}>
                                        <InfoLabel info={
                                            <>
                                                <div>
                                                    When enabled, customers can purchase the same product multiple times by default. When disabled, customers can purchase each product only once.
                                                </div>
                                                <div style={{ marginTop: "0.5rem", fontWeight: "500" }}>
                                                    We recommend configuring this setting on a per-product basis, as restricting repeat purchases is generally the safer option for most products.
                                                </div>
                                            </>
                                        }>
                                            Always allow repeat purchases
                                        </InfoLabel>
                                    </div>
                                    <div>
                                        <Switch size="small" defaultChecked={false} />
                                    </div>
                                    <div style={{ fontWeight: "500" }}>
                                        <InfoLabel info={
                                            <>
                                                <div>
                                                    Suspend all purchases from your store. Customers will be unable to make new purchases until the store is reopened. However, they will still be able to redeliver products they have previously purchased.
                                                </div>
                                            </>
                                        }>
                                            Close store
                                        </InfoLabel>
                                    </div>
                                    <div>
                                        <Switch size="small" defaultChecked={false} />
                                    </div>
                                </div>
                            </div> :
                        selectedTab === "staff" ?
                            <div>
                                <Card appearance="outline" style={{ marginBottom: "1rem" }}>
                                    <CardHeader header={
                                        <div className={styles.cardTitle}>
                                            <Info20Filled />
                                            <Subtitle2>Overview</Subtitle2>
                                        </div>
                                    } />

                                    Add and remove staff members who can edit your store settings and/or manage your products.
                                </Card>

                                <Toolbar>
                                    <Button icon={<AddFilled />} appearance="primary" onClick={() => setAddStaffMemberDrawerOpen(true)}>
                                        Add Staff Member
                                    </Button>
                                </Toolbar>
                                <Table>
                                    <TableHeader>
                                        <TableHeaderCell>Second Life Username</TableHeaderCell>
                                        <TableHeaderCell>Role</TableHeaderCell>
                                        <TableHeaderCell>Actions</TableHeaderCell>
                                    </TableHeader>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>
                                                <Link href="https://my.secondlife.com/abigail.slater" target="_blank">
                                                    Abigail Slater
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                {createRoleDropdown( "csr" )}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip content="Remove Staff Member" relationship="label">
                                                    <Button icon={<DeleteIcon />} size="small" appearance="subtle" />
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                <Link href="https://my.secondlife.com/ben.sheppard" target="_blank">
                                                    Ben Sheppard
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                {createRoleDropdown( "csr" )}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip content="Remove Staff Member" relationship="label">
                                                    <Button icon={<DeleteIcon />} size="small" appearance="subtle" />
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                                
                                <AddStaffMemberDrawer open={addStaffMemberDrawerOpen} setOpen={setAddStaffMemberDrawerOpen} />
                            </div> :
                        selectedTab === "api" ?
                            <div>
                                <Card appearance="outline" style={{ marginBottom: "1rem" }}>
                                    <CardHeader header={
                                        <div className={styles.cardTitle}>
                                            <Info20Filled />
                                            <Subtitle2>Overview</Subtitle2>
                                        </div>
                                    } />

                                    Using the in-world Omnivend API script, you can integrate your store with other systems and services. This allows for advanced automation and customization of your store's functionality.
                                </Card>
                                
                                <div className={styles.columnTable}>
                                    <div style={{ fontWeight: "500" }}>Store API key</div>
                                    <div>
                                        Unassigned
                                        &nbsp;
                                        <Tooltip content="Reassign API Key" relationship="label">
                                            <Button icon={<KeyResetIcon />} size="small" appearance="subtle" onClick={() => setReassignApiKeyDialogOpen(true)} />
                                        </Tooltip>
                                    </div>
                                    <div style={{ fontWeight: "500" }}>API documentation</div>
                                    <div>
                                        Coming Soon
                                    </div>
                                </div>
                            </div> :
                        selectedTab === "subscriptions" ?
                            <div>
                                <Card appearance="outline" style={{ marginBottom: "1rem" }}>
                                    <CardHeader header={
                                        <div className={styles.cardTitle}>
                                            <Info20Filled />
                                            <Subtitle2>Overview</Subtitle2>
                                        </div>
                                    } />

                                    Customers can rez a subscription kiosk to enable automatic payments for products from this store.
                                </Card>
                                
                                <div className={styles.columnTable}>
                                    <div style={{ fontWeight: "500" }}>
                                        Allow subscriptions
                                    </div>
                                    <div>
                                        <Switch size="small" defaultChecked={false} />
                                    </div>
                                </div>
                            </div> :
                        null
                    }
                </div>
            </div>
        </div>

        <ReassignApiKeyDialog open={reassignApiKeyDialogOpen} setOpen={setReassignApiKeyDialogOpen} triggerReassignApiKey={triggerReassignApiKey} />
        <Toaster toasterId="settingsToaster" />
    </>;
}